import React from 'react';
import PropTypes from 'prop-types';
import { consistentString } from '../../utils/content-formatting';
import ContentCard from '../content-card';
import RichText from '../rich-text/base';
import { Container } from '../styled/containers';
import { CTAAssembly } from '../cta';
import { BannerBackground, HeaderText, CardRow } from './styles';
import { useLayout } from '../layout/context';
import { RichTextPropTypes } from '../../prop-types';

/**
 * Displays a set of content cards inside a box with rich text header
 * Note: RichText as `base` does not support embedded entries.
 */
const ContentCardBanner = ({
  header,
  bannerColor,
  bannerFlow,
  contentCards = [],
  removeMarginBottom,
  cardsCTA,
  name,
}) => {
  const { insideSideBar } = useLayout();

  if (!contentCards || !contentCards.length) return null;

  const bannerDirection = insideSideBar ? 'vertical' : bannerFlow;

  return (
    <BannerBackground
      $bannerColor={bannerColor}
      $bannerFlow={bannerDirection}
      $sidebar={insideSideBar}
      $removeMarginBottom={removeMarginBottom}
    >
      <Container $padding={!insideSideBar}>
        {header && <HeaderText as={RichText} richText={header} />}
        <CardRow
          $items={contentCards.length}
          $removeMarginBottom={removeMarginBottom}
        >
          {contentCards.map((c, i) => (
            <ContentCard
              key={`${c.id}_${i}`}
              title={c.title}
              description={c.description}
              image={c.image}
              link={c.link}
              bannerFlow={bannerDirection}
              currentCardCount={i}
              cardCount={contentCards.length}
              rowName={name}
            />
          ))}
        </CardRow>
        {cardsCTA && (
          <CTAAssembly contentfulData={{ ...cardsCTA }} marginBottom={false} />
        )}
      </Container>
    </BannerBackground>
  );
};

ContentCardBanner.propTypes = {
  bannerColor: PropTypes.oneOf(['white', 'grey']),
  bannerFlow: PropTypes.oneOf(['horizontal', 'vertical', 'grid']),
  header: RichTextPropTypes,
  contentCards: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      link: PropTypes.object.isRequired,
      image: PropTypes.object,
    })
  ),
  cardsCTA: PropTypes.object,
  removeMarginBottom: PropTypes.bool,
  name: PropTypes.string,
};

ContentCardBanner.defaultProps = {
  bannerFlow: 'horizontal',
};

export default ContentCardBanner;

export const ContentCardBannerAssembly = ({ contentfulData }) => {
  // convert the color props into consistent strings
  const contentCards =
    contentfulData.contentCards &&
    contentfulData.contentCards.map((contentCard) => ({
      ...contentCard,
      color: consistentString(contentCard.color || 'red'),
    }));

  return (
    <ContentCardBanner
      bannerColor={consistentString(contentfulData.bannerColor)}
      bannerFlow="horizontal"
      header={contentfulData.header}
      contentCards={contentCards}
      removeMarginBottom={contentfulData.removeMarginBottom}
      cardsCTA={contentfulData.cardsCTA}
      name={contentfulData.name}
    />
  );
};

ContentCardBannerAssembly.propTypes = {
  contentfulData: PropTypes.object,
};
